/**
 * useSessionCheck Hook
 * Consolidates duplicate session-checking logic from Home.tsx and Layout.tsx
 *
 * Checks for active meal planning sessions in 'planning', 'shopping', or 'scheduling' status
 * Counts unassigned meals for sessions in 'scheduling' phase
 */

import { useState, useCallback } from 'react'
import { useUserId } from './useUserId'
import { backendApiService } from '../services/backendApiService'
import { SessionStatus } from '../types/session'
import { clientLogger } from '../utils/clientLogger'

/**
 * Meal session data
 */
export interface MealSession {
  id: string
  user_id: string | null
  guest_session_id: string | null
  status: SessionStatus
  session_preferences: any
  persistent_preferences: any
  created_at: string
  updated_at: string
}

/**
 * Session check result
 */
export interface SessionCheckResult {
  hasSession: boolean
  session: MealSession | null
  unassignedCount: number
}

/**
 * Hook return type
 */
export interface UseSessionCheckReturn extends SessionCheckResult {
  /**
   * Check for active sessions (planning, shopping, scheduling)
   * Returns updated session data
   */
  checkForActiveSession: () => Promise<SessionCheckResult>

  /**
   * Loading state (true while checking)
   */
  isChecking: boolean
}

/**
 * Hook: Check for active meal planning sessions
 *
 * Consolidates session-checking logic that was duplicated in Home.tsx and Layout.tsx
 * Use this hook when you need to:
 * - Check if user has an in-progress meal planning session
 * - Prompt user to resume or start fresh
 * - Count unassigned meals in scheduling phase
 *
 * @returns Session check result and checkForActiveSession function
 *
 * @example
 * const { hasSession, session, unassignedCount, checkForActiveSession } = useSessionCheck()
 *
 * const handlePlanClick = async () => {
 *   const result = await checkForActiveSession()
 *   if (result.hasSession) {
 *     showResumeModal(result.session)
 *   } else {
 *     startNewPlan()
 *   }
 * }
 */
export function useSessionCheck(): UseSessionCheckReturn {
  const userId = useUserId()
  const [hasSession, setHasSession] = useState(false)
  const [session, setSession] = useState<MealSession | null>(null)
  const [unassignedCount, setUnassignedCount] = useState(0)
  const [isChecking, setIsChecking] = useState(false)

  /**
   * Check for active sessions in 'planning', 'shopping', or 'scheduling' status
   * Excludes 'created' (no work done) and 'complete' (finished)
   */
  const checkForActiveSession = useCallback(async (): Promise<SessionCheckResult> => {
    if (!userId) {
      const result = { hasSession: false, session: null, unassignedCount: 0 }
      setHasSession(false)
      setSession(null)
      setUnassignedCount(0)
      return result
    }

    setIsChecking(true)

    try {
      // Check for sessions in active phases
      const response = await backendApiService.getMealSessionsByStatus(
        userId,
        ['planning', 'shopping', 'scheduling']
      )

      if (response.sessions?.length > 0) {
        const activeSession = response.sessions[0] // Get most recent

        // If in scheduling phase, count unassigned meals
        let unassigned = 0
        if (activeSession.status === 'scheduling') {
          try {
            const recipes = await backendApiService.getSessionRecipes(activeSession.id)
            unassigned = recipes.filter(r =>
              !r.assigned_dates || r.assigned_dates.length === 0
            ).length

            clientLogger.debug({ unassigned }, 'Found unassigned meals in scheduling session')
          } catch (error) {
            clientLogger.error({ err: error instanceof Error ? error : new Error(String(error)) }, 'Error fetching unassigned count')
            unassigned = 0
          }
        }

        // Update state
        setHasSession(true)
        setSession(activeSession)
        setUnassignedCount(unassigned)

        const result = {
          hasSession: true,
          session: activeSession,
          unassignedCount: unassigned
        }

        clientLogger.debug({ status: activeSession.status }, 'Found active session')
        return result
      }

      // No active session found
      const result = { hasSession: false, session: null, unassignedCount: 0 }
      setHasSession(false)
      setSession(null)
      setUnassignedCount(0)

      clientLogger.debug({}, 'No active session found')
      return result

    } catch (error) {
      clientLogger.error({ err: error instanceof Error ? error : new Error(String(error)) }, 'Error checking for active sessions')

      // Return "no session" on error (graceful fallback)
      const result = { hasSession: false, session: null, unassignedCount: 0 }
      setHasSession(false)
      setSession(null)
      setUnassignedCount(0)

      return result
    } finally {
      setIsChecking(false)
    }
  }, [userId])

  return {
    hasSession,
    session,
    unassignedCount,
    checkForActiveSession,
    isChecking
  }
}